import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { GraphGradientEnums } from "../graph-gradient-enum";
import { FC } from "react";

interface DashboardTrandingChartProps {
  seriesData: [];
  xAxisCategory: [];
  xToolTip: string;
  yToolTip: string;
  extraData?: [];
  extraTitle?: string[];
  isShowLevel?: boolean;
  isAllZero?: boolean;
  hr?: string;
  onPointClick?: (xValue: string) => void; // Add click handler prop
}

const createTooltipFormatter = (
  xToolTip: string,
  yToolTip: string,
  hr: string,
  extraData?: [],
  extraTitle?: string[],
) =>
  function (this: Highcharts.TooltipFormatterContextObject) {
    const xValue = this.x;
    const yValue = this.y;
    let toolTip = `<strong>${xToolTip}:</strong> ${xValue}<br><strong>${yToolTip}:</strong> ${yValue} ${hr}`;

    if (extraTitle?.length && extraData?.length) {
      extraData.forEach((data: any, index: number) => {
        const extraValue = data[this.point.index];
        toolTip += `<br><strong>${extraTitle[index]}:</strong> ${extraValue}`;
      });
    }

    return toolTip;
  };

const DashboardTrandingChart: FC<DashboardTrandingChartProps> = ({
  seriesData,
  xAxisCategory,
  xToolTip,
  yToolTip,
  extraData,
  extraTitle,
  isAllZero = false,
  isShowLevel = true,
  hr = "",
  onPointClick,
}: any) => {
  const chartOptions = {
    chart: {
      height: 120,
      style: {
        fontFamily: '"Cabin", Helvetica, Arial, sans-serif',
      },
      lang: {
        noData: "No data available",
      },
      animation: {
        duration: 1000,
      },
      zooming: {
        type: "xy",
      },
    },
    title: {
      text: "",
    },

    credits: {
      enabled: false,
    },
    xAxis: {
      categories: xAxisCategory,
      tickmarkPlacement: "on", // Place the tick marks on the categories
      labels: {
        enabled: isShowLevel, // Enable labels to show months
      },
      lineWidth: 0, // Hide the X-axis line
      tickLength: 0, // Hide tick marks
    },
    yAxis: {
      labels: {
        enabled: false, // Hide y-axis labels
      },
      min: 0,
      tickAmount: 2,
      title: {
        text: null, // Remove y-axis title
      },
      gridLineWidth: 0, // Remove y-axis grid lines
    },
    tooltip: {
      formatter: createTooltipFormatter(xToolTip, yToolTip, hr, extraData, extraTitle),
    },
    plotOptions: {
      series: {
        label: {
          connectorAllowed: false,
        },
        marker: {
          enabled: true, // Show data point indicators
          fillColor: GraphGradientEnums.LIGHTER_BLUE, // Data point fill color (hex code for a red-orange color)
          // lineColor: "#C70039", // Data point border color (hex code for a deep red color)
          // lineWidth: 0.5, // Thickness of the border around the data point
        },
        fillColor: {
          linearGradient: [0, 0, 0, 300], // Define the gradient direction
          stops: [
            [0.1, GraphGradientEnums.DARKER_BLUE], // Very dark blue
            [0.2, GraphGradientEnums.LIGHTER_BLUE], // Even darker blue
            [0.3, GraphGradientEnums.LIGHTEST_BLUE], // Darker blue
            [0.4, GraphGradientEnums.LIGHTEST_BLUE], // Deeper blue
            [0.5, GraphGradientEnums.LIGHTEST_BLUE], // Medium blue
            [0.6, GraphGradientEnums.LIGHTEST_BLUE], // Medium light blue
            [0.7, GraphGradientEnums.LIGHTEST_BLUE], // Lighter blue
            [0.8, GraphGradientEnums.LIGHTEST_BLUE], // Light blue
            [1, GraphGradientEnums.LIGHTEST_BLUE], // Lightest blue
          ],
        },
        lineColor: GraphGradientEnums.LINE_COLOR,
        threshold: null, // Allow shading below data points
        point: {
          events: {
            click: function (event: Highcharts.PointClickEventObject) {
              // Get x-axis value from clicked point
              const point = event.point;
              const xValue = point.series.xAxis.categories[point.x];
              if (onPointClick) {
                onPointClick(xValue);
              }
            },
          },
        },
      },
    },
    legend: {
      enabled: false,
    },
    exporting: {
      enabled: false, // Disable the hamburger icon (exporting menu)
    },
    series: {
      type: isAllZero ? "line" : "areaspline",
      data: seriesData,
      showInLegend: true,
      name: "Energy Consumption",
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              align: "center",
              verticalAlign: "bottom",
            },
          },
        },
      ],
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

export default DashboardTrandingChart;
